import axios from 'axios';
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { getAuthority, loginRequest, msalConfig } from '../authConfig';
import configData from '../config.json';
import { appInsights } from '../services/ApplicationInsightsService';
import { ACCESS_TOKEN_NAME } from '../utils/enums';
// eslint-disable-next-line import/no-cycle
import { isTokenExpired } from '../utils/helpers';
import { logAppError, logAuthStep } from '../utils/logActions';

const searchParams = new URLSearchParams(window.location.search);
const idTokenHint = searchParams.get('id_token_hint');
const authority = getAuthority(idTokenHint);
const msalInstance = new PublicClientApplication(msalConfig(authority));

export const API_URL = `${configData.REACT_APP_BACKEND_URL}/api`;
export const TNE_API_URL = `${configData.REACT_APP_TANDE_URL}/api`;
export const IDENTITY_URL = configData.REACT_APP_IDENTITY_URL;

const isQuickApplyEmbedded = () => window.location.pathname.startsWith('/quickapply-embedded');

const isStoredTokenExpired = (token) => {
  try {
    return isTokenExpired(token);
  } catch (e) {
    // Malformed token - treat as expired so it gets replaced
    return true;
  }
};

// Single in-flight refresh shared by all concurrent requests
let refreshPromise = null;

// Returns a fresh access token via the MSAL refresh token, or null if there is no signed-in account.
// Throws when MSAL cannot refresh silently (e.g. InteractionRequiredAuthError).
function refreshAccessToken({ forceRefresh = false } = {}) {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) return Promise.resolve(null);

  if (!refreshPromise) {
    refreshPromise = msalInstance
      .acquireTokenSilent({ ...loginRequest, account, forceRefresh })
      .then((tokenResponse) => {
        localStorage.setItem(ACCESS_TOKEN_NAME, tokenResponse.accessToken);
        logAuthStep('API_Token_Refreshed');
        return tokenResponse.accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

let reAuthInProgress = false;

// Session can't be recovered silently - drop the stale token and send the user to sign in once
function redirectToLogin(error) {
  localStorage.removeItem(ACCESS_TOKEN_NAME);
  if (reAuthInProgress || isQuickApplyEmbedded()) return;
  reAuthInProgress = true;

  appInsights.trackException({
    name: 'Re-authentication required, redirecting to login',
    error
  });

  msalInstance.loginRedirect({ ...loginRequest, redirectUri: `${window.location.origin}/dashboard` }).catch((e) => {
    reAuthInProgress = false;
    logAppError(e, 'API_ReAuth_LoginRedirect');
  });
}

async function getValidAccessToken() {
  const token = localStorage.getItem(ACCESS_TOKEN_NAME);
  if (!token || !isStoredTokenExpired(token)) return token;

  try {
    const freshToken = await refreshAccessToken();
    if (!freshToken) {
      // Expired token with no signed-in account - don't send it
      localStorage.removeItem(ACCESS_TOKEN_NAME);
    }
    return freshToken;
  } catch (error) {
    logAppError(error, 'API_Token_Refresh_Before_Request');
    if (error instanceof InteractionRequiredAuthError) {
      redirectToLogin(error);
    }
    return null;
  }
}

async function handle401Error(error, client) {
  const originalRequest = error.config;

  if (!originalRequest || originalRequest.authRetried) {
    redirectToLogin(error);
    return Promise.reject(error);
  }
  originalRequest.authRetried = true;

  try {
    // Backend rejected the token, so don't accept MSAL's cached one
    const accessToken = await refreshAccessToken({ forceRefresh: true });
    if (!accessToken) {
      redirectToLogin(error);
      return Promise.reject(error);
    }
    originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
    return client(originalRequest);
  } catch (refreshError) {
    logAppError(refreshError, 'API_401_Token_Refresh');
    if (refreshError instanceof InteractionRequiredAuthError) {
      redirectToLogin(refreshError);
    }
    return Promise.reject(error);
  }
}

const createAxiosClient = (baseURL) => {
  const client = axios.create({ baseURL, withCredentials: true });

  client.interceptors.request.use(
    async (config) => {
      const token = await getValidAccessToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json;charset=utf-8';
      }

      config.headers['Access-Control-Allow-Origin'] = '*';
      config.headers['Access-Control-Allow-Credentials'] = 'true';
      config.headers['Access-Control-Allow-Methods'] = 'GET,PUT,PATCH,POST,DELETE,HEAD,OPTIONS';

      return config;
    },
    (error) => {
      logAppError('API_Request_Error_in_Request', { error });
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        return handle401Error(error, client);
      }

      return Promise.reject(error);
    }
  );

  return client;
};

export const axiosClient = createAxiosClient(API_URL);
export const tneAxiosClient = createAxiosClient(TNE_API_URL);
export const identityAxiosClient = createAxiosClient(IDENTITY_URL);

export default axiosClient;
